import { Formik } from 'formik';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, ICustomer, ICustomerWithVehicles, IVehicle } from '../../API/GoMoto';
import { AccountType, DMSType } from '../../API/Enums';
import {
    useAppointmentUpdateAndPersist,
    useEnsureAppointment,
    useTypedInFields,
    useAppointmentDispatch,
    useMaskPhone,
    AppointmentDispatchTypes,
} from '../../context/Appointment/appointment';
import { KeyboardDispatchTypes, useKeyboardDispatch } from '../../context/Keyboard/keyboard';
import { useFullReset, useKioskState } from '../../context/Kiosk/kiosk';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { ScrollTo } from '../../controls/ScrollTo/ScrollTo/ScrollTo';
import { SearchListMultiResult } from '../../controls/SearchListResults/SearchListResult/SearchListMultiResult';
import { SearchListResult } from '../../controls/SearchListResults/SearchListResult/SearchListResult';
import { Spinner } from '../../controls/Spinner/Spinner';
import { getFullDisplayName } from '../../helpers/Appointment';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { useNotListedReducer } from '../../helpers/notListedReducer';
import { formatAsPhoneNumber } from '../../helpers/Number';
import { useDisplayLocalizedVehicle } from '../../helpers/displayVehicle';
import { default as css } from './Lookup.module.scss';
import { AppointmentRoutePaths, AppRoutePaths, Customer_AddRoutesPaths, CustomerRoutePaths, PathName } from '../../helpers/Routes';
import { AppointmentLookupMethod } from '../Appointment/Search/Lookup';
import { nameof } from '../../helpers/nameof';
import { GMFormPhoneField, GMFormPhoneOrEmailField, GMFormTextField } from '../../controls/TextField';
import { GMDialogError } from '../../controls/Dialog';
import { IconTypes } from '../../controls/Icon/GenericIcon/GenericIcon.types';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';
import { GMRadio, GMRadioGroup } from '../../controls/Radio';
import { GMForm, GMFormWrapper } from '../../controls/Form';
import '../../helpers/Phone';

export const Lookup = React.memo(() => {
    const Localization = useLocalization();
    const [customers, setCustomers] = useState<ICustomerWithVehicles[]>();
    const {
        key: kioskKey,
        dealer: { key: dealerKey, newCustomerEnabled = true, dmsType },
    } = useKioskState();
    const navigate = useNavigate();
    const { appointment } = useEnsureAppointment();
    const setAppointment = useAppointmentUpdateAndPersist();
    const appointmentDispatch = useAppointmentDispatch();
    const maskPhone = useMaskPhone();
    const isKiosk = isElectron();
    const requireFullSearch = !isKiosk;
    const [seeServiceAdvisor, setSeeServiceAdvisor] = useState(false);
    const { start: startNotListed, complete: notListedComplete } = useNotListedReducer(appointment);
    const [showNotFoundError, setShowNotFoundError] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const typedInFields = useTypedInFields();
    const reset = useFullReset();
    const [loading, setLoading] = useState<boolean>(false);
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();

    React.useEffect(() => {
        notListedComplete && navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Add));
    }, [navigate, notListedComplete]);

    const initialValues: {
        accountType: AccountType;
        phoneOrEmail: string;
        firstName: string;
        lastName: string;
        business: string;
        selectedVehicle?: IVehicle;
        notListed: boolean;
        customerIndex?: number;
        vehicleIndex?: number;
        selectedCustomer?: ICustomer;
    } = {
        accountType: appointment?.accountType || AccountType.individual,
        phoneOrEmail: typedInFields?.phoneNumber || typedInFields?.email || appointment?.phone || appointment?.email || '',
        firstName: typedInFields?.firstName || appointment?.firstName || '',
        business: typedInFields?.businessName || appointment?.businessName || '',
        lastName: typedInFields?.lastName || appointment?.lastName || '',
        notListed: false,
    };

    const keyboardDispatch = useKeyboardDispatch();

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values) => {
                    switch (values.accountType) {
                        case AccountType.business:
                            values.firstName = '';
                            values.lastName = '';
                            break;
                        case AccountType.individual:
                            values.business = '';
                            break;
                    }

                    const phoneSearch = values.phoneOrEmail !== null ? !(/[^0-9]/).test(values.phoneOrEmail) : null;

                    appointmentDispatch({
                        type: AppointmentDispatchTypes.TypedInFields,
                        payload: {
                            firstName: values.firstName,
                            lastName: values.lastName,
                            businessName: values.business,
                            phoneNumber: phoneSearch ? values.phoneOrEmail : undefined,
                            email: phoneSearch ? undefined : values.phoneOrEmail,
                        },
                    });

                    if (values.notListed) {
                        if (newCustomerEnabled) {
                            const { firstName, lastName, business, accountType, phoneOrEmail } = values;
                            startNotListed({
                                firstName,
                                lastName,
                                businessName: business,
                                accountType,
                                phone: phoneSearch ? phoneOrEmail : '',
                                email: phoneSearch ? undefined : phoneOrEmail,
                            });
                        }
                        else {
                            setSeeServiceAdvisor(true);
                        }
                    }
                    else if (values.selectedCustomer && appointment) {
                        const {
                            firstName,
                            lastName,
                            businessName,
                            businessType,
                            city,
                            street,
                            streetLine2,
                            postalCode,
                            stateOrProvince,
                            email,
                            phone,
                            cellPhone,
                            phoneTypeEnum,
                            contactPreference,
                            accountType,
                            dmsCustomerKey,
                            allPhones,
                        } = values.selectedCustomer;
                        const newAppointment = {
                            ...appointment,
                            dealerKey,
                            firstName,
                            lastName,
                            businessName,
                            businessType,
                            city,
                            street,
                            streetLine2,
                            postalCode,
                            stateOrProvince,
                            email,
                            phone,
                            cellPhone,
                            phoneTypeEnum,
                            contactPreference,
                            accountType,
                            dmsCustomerKey,
                            allPhones,
                        } as typeof appointment;

                        // mask PII (except phone if exact match with what customer entered)
                        appointmentDispatch({
                            type: AppointmentDispatchTypes.MaskField,
                            payload: 'all',
                        });
                        if (phoneSearch) {
                            if (values.phoneOrEmail === phone) {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.UnmaskField,
                                    payload: 'phone',
                                });
                            }
                        }
                        else if (values.phoneOrEmail.toLowerCase() === email.toLowerCase()) {
                            appointmentDispatch({
                                type: AppointmentDispatchTypes.UnmaskField,
                                payload: 'email',
                            });
                        }

                        if (values.vehicleIndex === undefined) {
                            return setAppointment(newAppointment)
                                .then(() => {
                                    navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Add, Customer_AddRoutesPaths.Details));
                                });
                        }
                        else if (values.selectedVehicle) {
                            const {
                                year,
                                make,
                                model,
                                chromeMake,
                                chromeModel,
                                series,
                                trim,
                                style,
                                mileage,
                                vin,
                                dmsVehicleKey,
                                exteriorColor,
                                interiorColor,
                            } = values.selectedVehicle;

                            return setAppointment(
                                {
                                    ...newAppointment,

                                    year: '' + year,
                                    make,
                                    model,
                                    chromeMake,
                                    chromeModel,
                                    series: (series ?? '').trim() !== '' ? series : trim,
                                    style,
                                    mileage,
                                    vin,
                                    dmsVehicleKey: dmsVehicleKey || vin,
                                    exteriorColor,
                                    interiorColor,
                                },
                                true,
                            )
                                .then(() => {
                                    navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Details));
                                });
                        }
                    }
                    else if (isKiosk && hasSearched) {
                        navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Search, AppointmentLookupMethod.PhoneOrEmail, 'go'));
                    }
                    else if (!loading) {
                        setLoading(true);

                        return getCustomers(kioskKey, dealerKey, {
                            phone: phoneSearch ? values.phoneOrEmail : '',
                            email: phoneSearch ? '' : values.phoneOrEmail,
                            lastName: values.lastName,
                            business: values.business,
                            firstName: values.firstName,
                        })
                            .then((results) => {
                                if (!isKiosk && (!results || results.length === 0)) {
                                    setShowNotFoundError(true);
                                }
                                setCustomers(results);
                                setHasSearched(true);
                            })
                            .catch(() => {
                                /*
                                 * On a DMS timeout/error, fall through to the empty-result state so the
                                 * consumer can still proceed: on kiosk an empty list renders the
                                 * "I do not see myself listed" walk-in option; on web/mobile we surface
                                 * the not-found dialog. Without this, the search would dead-end with no
                                 * results rendered and no way forward.
                                 */
                                if (!isKiosk) {
                                    setShowNotFoundError(true);
                                }
                                setCustomers([]);
                                setHasSearched(true);
                            })
                            .finally(() => {
                                setLoading(false);
                            });
                    }
                }}
            >
                {({ values, setValues, isSubmitting, setFieldValue, submitCount, submitForm }) => {
                    if (submitCount === 0) {
                        if ((!requireFullSearch && appointment?.phone) || (isKiosk && appointment?.email)) {
                            submitForm();
                        }
                        else if (requireFullSearch && (appointment?.firstName || appointment?.businessName)) {
                            submitForm();
                        }
                    }

                    const customerResults = customers?.map((customer, custIndex) => {
                        // Make sure the vin is not null
                        customer.vehicles?.forEach((vehicle) => {
                            if (!vehicle.vin) {
                                vehicle.vin = '';
                            }
                        });

                        const listItems =
                            customer.vehicles?.map((vehicle, vehIndex) => ({
                                title: displayLocalizedVehicle({
                                    ...vehicle,
                                    year: '' + (vehicle.year ?? ''),
                                })
                                    .toString(),
                                vinTitle: !vehicle.vin || vehicle.vin.slice(-1) === '~' ? '' : `(VIN ${vehicle.vin.slice(-6)})`,
                                onClick: () => {
                                    const { vehicles, ...customerRest } = customer;

                                    if (values.customerIndex === custIndex && values.vehicleIndex === vehIndex) {
                                        setValues({
                                            ...values,
                                            selectedVehicle: undefined,
                                            notListed: false,
                                            selectedCustomer: undefined,
                                            customerIndex: undefined,
                                            vehicleIndex: undefined,
                                        });
                                    }
                                    else {
                                        setValues({
                                            ...values,
                                            selectedVehicle: {
                                                ...vehicle,
                                                year: '' + (vehicle.year ?? ''),
                                            },
                                            notListed: false,
                                            selectedCustomer: customerRest,
                                            customerIndex: custIndex,
                                            vehicleIndex: vehIndex,
                                        });
                                    }
                                },
                                checked: values.customerIndex === custIndex && values.vehicleIndex === vehIndex,
                            })) || [];

                        listItems.push({
                            title: Localization.NewVehicle,
                            vinTitle: '',
                            onClick: () => {
                                if (values.customerIndex === custIndex && !values.selectedVehicle) {
                                    setValues({
                                        ...values,
                                        selectedVehicle: undefined,
                                        notListed: false,
                                        selectedCustomer: undefined,
                                        customerIndex: undefined,
                                        vehicleIndex: undefined,
                                    });
                                }
                                else {
                                    setValues({
                                        ...values,
                                        selectedVehicle: undefined,
                                        notListed: false,
                                        selectedCustomer: customer,
                                        customerIndex: custIndex,
                                        vehicleIndex: undefined,
                                    });
                                }
                            },
                            checked: !values.selectedVehicle && values.customerIndex === custIndex,
                        });

                        return (
                            <SearchListMultiResult
                                key={customer.key ?? custIndex}
                                title={getFullDisplayName(customer)}
                                isBusiness={customer.businessName !== null}
                                icon={
                                    <GMSizedIcon
                                        iconSize={'large'}
                                        iconType={'User'}
                                        ariaLabel={Localization.UserIcon}
                                    />
                                }
                                listItems={listItems}
                            >
                                <div className={css.phoneNumber}>{formatAsPhoneNumber(customer.phone, values.phoneOrEmail !== customer.phone)}</div>
                            </SearchListMultiResult>
                        );
                    });

                    let primaryHeading = '';
                    let icon: IconTypes;
                    let ariaLabel: string = '';

                    if (requireFullSearch) {
                        switch (values.accountType) {
                            case AccountType.individual:
                                primaryHeading = Localization.WhatIsYourNamePhone;
                                icon = 'Phone';
                                ariaLabel = Localization.PhoneIcon;
                                break;
                            case AccountType.business:
                                primaryHeading = Localization.LookupBusiness;
                                icon = 'Business';
                                ariaLabel = Localization.BusinessIcon;
                                break;
                        }
                    }
                    else if (isKiosk) {
                        if (dmsType === DMSType.DEALERBUILT) {
                            primaryHeading = Localization.PleaseEnterPhoneNumber;
                        }
                        else {
                            primaryHeading = Localization.PleaseEnterPhoneNumberOrEmail;
                        }
                        icon = 'Profile';
                    }
                    else {
                        primaryHeading = Localization.WhatIsYourPhoneNumber;
                        icon = 'Phone';
                    }

                    return (
                        <>
                            <GMForm
                                submitButton={
                                    <CTAButton
                                        type={'submit'}
                                        styleType={'primary'}
                                        title={
                                            (values.vehicleIndex !== undefined && values.customerIndex !== undefined)
                                                || values.notListed
                                                || (!values.selectedVehicle && values.customerIndex !== undefined)
                                                ? Localization.ClickToProceedToNextStep
                                                : undefined
                                        }
                                        label={
                                            (values.vehicleIndex !== undefined && values.customerIndex !== undefined)
                                                || values.notListed
                                                || (!values.selectedVehicle && values.customerIndex !== undefined)
                                                ? Localization.Proceed
                                                : Localization.Search
                                        }
                                        onClick={() => keyboardDispatch({
                                            type: KeyboardDispatchTypes.KeyboardHidden,
                                            payload: true,
                                        })}
                                        id={
                                            (values.vehicleIndex !== undefined && values.customerIndex !== undefined)
                                                || values.notListed
                                                || (!values.selectedVehicle && values.customerIndex !== undefined)
                                                ? Localization.Proceed
                                                : Localization.Search
                                        }
                                        ariaLabel={
                                            (values.vehicleIndex !== undefined && values.customerIndex !== undefined)
                                                || values.notListed
                                                || (!values.selectedVehicle && values.customerIndex !== undefined)
                                                ? Localization.Proceed
                                                : Localization.Search
                                        }
                                    />
                                }
                                heading={
                                    <Heading
                                        roundedIcon={
                                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                                <GMSizedIcon
                                                    iconType={icon}
                                                    iconSize={'extraLarge'}
                                                    ariaLabel={ariaLabel}
                                                />
                                            </GMRoundedIcon>
                                        }
                                        secondaryHeading={primaryHeading}
                                    />
                                }
                            >
                                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                                {(requireFullSearch && (
                                    <>
                                        <GMRadioGroup name={nameof<typeof values>('accountType')} ariaLabel={nameof<typeof values>('accountType')}>
                                            <GMRadio value={AccountType.individual} label={Localization.Individual} />
                                            <GMRadio value={AccountType.business} label={Localization.Business} />
                                        </GMRadioGroup>
                                        {values.accountType === AccountType.business ? (
                                            <GMFormTextField
                                                name={nameof<typeof values>('business')}
                                                id={Localization.Business}
                                                title={Localization.Business}
                                                label={Localization.Business}
                                                ariaLabel={Localization.Business}
                                                autoFocus
                                                required
                                                onChangeCallback={() => {
                                                    setValues({
                                                        ...values,
                                                        selectedVehicle: undefined,
                                                        notListed: false,
                                                        customerIndex: undefined,
                                                        vehicleIndex: undefined,
                                                        selectedCustomer: undefined,
                                                    });
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <GMFormTextField
                                                    name={nameof<typeof values>('firstName')}
                                                    id={Localization.FirstName}
                                                    title={Localization.FirstName}
                                                    label={Localization.FirstName}
                                                    ariaLabel={Localization.FirstName}
                                                    autoFocus
                                                    required
                                                    onChangeCallback={() => {
                                                        setValues({
                                                            ...values,
                                                            selectedVehicle: undefined,
                                                            notListed: false,
                                                            customerIndex: undefined,
                                                            vehicleIndex: undefined,
                                                            selectedCustomer: undefined,
                                                        });
                                                    }}
                                                />
                                                <GMFormTextField
                                                    name={nameof<typeof values>('lastName')}
                                                    id={Localization.LastName}
                                                    title={Localization.LastName}
                                                    label={Localization.LastName}
                                                    ariaLabel={Localization.LastName}
                                                    required
                                                    onChangeCallback={() => {
                                                        setValues({
                                                            ...values,
                                                            selectedVehicle: undefined,
                                                            notListed: false,
                                                            customerIndex: undefined,
                                                            vehicleIndex: undefined,
                                                            selectedCustomer: undefined,
                                                        });
                                                    }}
                                                />
                                            </>
                                        )}
                                        <GMFormPhoneField
                                            name={nameof<typeof values>('phoneOrEmail')}
                                            id={Localization.PhoneNumber}
                                            title={Localization.PhoneNumber}
                                            label={Localization.PhoneNumber}
                                            ariaLabel={Localization.PhoneNumber}
                                            required
                                            initiallyMasked={maskPhone}
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedVehicle: undefined,
                                                    notListed: false,
                                                    customerIndex: undefined,
                                                    vehicleIndex: undefined,
                                                    selectedCustomer: undefined,
                                                });
                                            }}
                                        />
                                    </>
                                )) ||
                                    (isKiosk && dmsType !== DMSType.DEALERBUILT ? (
                                        <GMFormPhoneOrEmailField
                                            name={nameof<typeof values>('phoneOrEmail')}
                                            id={Localization.PhoneNumberOrEmail}
                                            title={Localization.PhoneNumberOrEmail}
                                            label={Localization.PhoneNumberOrEmail}
                                            ariaLabel={Localization.PhoneNumberOrEmail}
                                            required
                                            initiallyMasked={maskPhone}
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedVehicle: undefined,
                                                    notListed: false,
                                                    customerIndex: undefined,
                                                    vehicleIndex: undefined,
                                                    selectedCustomer: undefined,
                                                });
                                            }}
                                        />
                                    ) : (
                                        <GMFormPhoneField
                                            name={nameof<typeof values>('phoneOrEmail')}
                                            id={Localization.PhoneNumber}
                                            title={Localization.PhoneNumber}
                                            label={Localization.PhoneNumber}
                                            ariaLabel={Localization.PhoneNumber}
                                            required
                                            initiallyMasked={maskPhone}
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedVehicle: undefined,
                                                    notListed: false,
                                                    customerIndex: undefined,
                                                    vehicleIndex: undefined,
                                                    selectedCustomer: undefined,
                                                });
                                            }}
                                        />
                                    ))}
                                {(isSubmitting || loading) && <Spinner />}

                                {!isSubmitting && !loading && customerResults && (
                                    <ScrollTo scroll={true}>
                                        {customerResults}
                                        <div className={css.notListed}>
                                            <SearchListResult
                                                title={Localization.IDoNotSeeMyselfListed}
                                                onClick={() => {
                                                    if (values.notListed) {
                                                        setValues({
                                                            ...values,
                                                            selectedVehicle: undefined,
                                                            notListed: false,
                                                            customerIndex: undefined,
                                                            vehicleIndex: undefined,
                                                            selectedCustomer: undefined,
                                                        });
                                                    }
                                                    else {
                                                        setValues({
                                                            ...values,
                                                            selectedVehicle: undefined,
                                                            notListed: true,
                                                            customerIndex: undefined,
                                                            vehicleIndex: undefined,
                                                            selectedCustomer: undefined,
                                                        });
                                                    }
                                                }}
                                                checked={values.notListed}
                                                headerLevel={5}
                                                notListed
                                            />
                                        </div>
                                    </ScrollTo>
                                )}
                            </GMForm>

                            <GMDialogError
                                ariaLabel={Localization.PleaseSeeAServiceAdvisorToCheckIn}
                                open={seeServiceAdvisor}
                                onClose={reset}
                            >
                                {Localization.PleaseSeeAServiceAdvisorToCheckIn}
                            </GMDialogError>

                            <GMDialogError
                                ariaLabel={Localization.CustomerNotFound}
                                open={showNotFoundError}
                                onClose={() => {
                                    setShowNotFoundError(false);
                                }}
                            >
                                {Localization.CustomerNotFound}
                            </GMDialogError>
                        </>
                    );
                }}
            </Formik>
        </>
    );
});
